import RawDataAPI from '../../api/rawData';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isImporting: false,
    isResolving: false,
  },
  meta: {
    totalCount: 0,
    currentPage: 1,
    perPage: 15,
  },
};

const getters = {
  getRecords: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getRecord: $state => id => $state.records.find(r => r.id === Number(id)),
};

const mutations = {
  SET_RAW_DATA($state, { data, meta }) {
    $state.records = data;
    if (meta) {
      $state.meta = {
        totalCount: meta.total_count,
        currentPage: meta.current_page,
        perPage: meta.per_page,
      };
    }
  },
  ADD_RAW_DATUM($state, datum) {
    $state.records.push(datum);
  },
  UPDATE_RAW_DATUM($state, datum) {
    const idx = $state.records.findIndex(r => r.id === datum.id);
    if (idx !== -1) {
      $state.records.splice(idx, 1, datum);
    }
  },
  REMOVE_RAW_DATUM($state, id) {
    $state.records = $state.records.filter(r => r.id !== id);
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

const actions = {
  async get({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await RawDataAPI.get(params);
      commit('SET_RAW_DATA', { data: data.payload, meta: data.meta });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async create({ commit }, rawDatum) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await RawDataAPI.create(rawDatum);
      commit('ADD_RAW_DATUM', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...attrs }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await RawDataAPI.update(id, attrs);
      commit('UPDATE_RAW_DATUM', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await RawDataAPI.delete(id);
      commit('REMOVE_RAW_DATUM', id);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async search({ commit }, params) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await RawDataAPI.search(params);
      commit('SET_RAW_DATA', { data: data.payload, meta: data.meta });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async filter({ commit }, params) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await RawDataAPI.filter(params);
      commit('SET_RAW_DATA', { data: data.payload, meta: data.meta });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async importFile({ commit }, { file, source }) {
    commit('SET_UI_FLAG', { isImporting: true });
    try {
      const { data } = await RawDataAPI.import(file, source);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isImporting: false });
    }
  },

  async bulkResolve({ commit }, { inboxId, rawDatumIds }) {
    commit('SET_UI_FLAG', { isResolving: true });
    try {
      const { data } = await RawDataAPI.bulkResolve({ inboxId, rawDatumIds });
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isResolving: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
